import {useContext, useState} from 'react'

import {useSuiContext} from '@s-ui/react-context'
import {useMount} from '@s-ui/react-hooks'
import {useLocation, useRouter} from '@s-ui/react-router'
import {AuthenticationContext} from '@adv-mt/mt-context-authentication'
import {LoginModalContext} from '@adv-mt/mt-context-login-modal'

import {ROUTES} from '../../../../../config/index.js'
import {NOTIFICATIONS} from '../notifications.js'

// Same values as the drawer's own content types, kept here so the page does not import the drawer chunk.
export const RESERVATION_DRAWER_CONTENTS = {
  INFO: 'reservationInfo',
  TERMS: 'reservationTerms'
}

/**
 * Acts on the hash Reservation/Init appends to the ad URL, mirroring the legacy detail:
 * `#login` opens the login modal, the wrong-user / not-available hashes raise a toast, and the
 * drawer hashes are returned so the reservation panel can open itself.
 *
 * @returns {{reservationDrawerContentType: string|null}} drawer content type to open on mount, if any
 */
const useReservationHash = ({adId, notify}) => {
  const {domain} = useSuiContext()
  const {hash, pathname} = useLocation()
  const {push, replace} = useRouter()
  const {user} = useContext(AuthenticationContext)
  const {triggerLoginModal} = useContext(LoginModalContext)

  const config = domain.get('config')
  const {AD_DETAIL_RESERVATION_HASHES: RESERVATION_HASHES} = config.get('page')

  // The hash is read once: a later navigation inside the page must not reopen the drawer.
  const [reservationDrawerContentType] = useState(() => {
    if (hash.startsWith(RESERVATION_HASHES.FAQ)) return RESERVATION_DRAWER_CONTENTS.TERMS
    if (hash.startsWith(RESERVATION_HASHES.OPEN_DRAWER)) return RESERVATION_DRAWER_CONTENTS.INFO
    return null
  })

  useMount(() => {
    if (hash === RESERVATION_HASHES.WRONG_USER) notify(NOTIFICATIONS.RESERVATION_WRONG_USER)
    if (hash === RESERVATION_HASHES.NOT_AVAILABLE) notify(NOTIFICATIONS.RESERVATION_NOT_AVAILABLE)

    if (hash !== RESERVATION_HASHES.LOGIN) return

    replace(pathname)
    const needsLoginOrVerification = !user || user.isEmailConfirmed === false
    if (!needsLoginOrVerification) return

    const {LOGIN_SOURCE_ID_RESERVATIONS} = config.get('login')
    const {VERIFICATION_REMINDER} = config.get('LOGIN_STEPS')
    const {HIT_ORIGINS} = config.get('tracking')
    triggerLoginModal({
      modalProps: {
        source: LOGIN_SOURCE_ID_RESERVATIONS,
        trackingOrigin: HIT_ORIGINS.RESERVATION,
        onLogin: () => push(`${ROUTES.RESERVATION.PAYMENT}?adId=${adId}`),
        enableVerificationReminder: true,
        initialStep: user?.isEmailConfirmed === false ? VERIFICATION_REMINDER : undefined
      }
    })
  })

  return {reservationDrawerContentType}
}

export default useReservationHash
